import React, { useState, useEffect, useRef, useCallback } from "react";
import { Typography, Box, Button } from '@mui/material';
import axios from "axios";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GeneralInformationBox from "../../Utilities/NCProForms/GeneralInformationBox";
import AffectedItems from "../../Utilities/NCProForms/AffectedItems";
import NonConformanceDescription from "../../Utilities/NCProForms/NonConformanceDescription";
import AdditionalInformation from "../../Utilities/NCProForms/AdditionalInformation";
import toast from 'react-hot-toast';
import ProgressStepper from "../../Utilities/ProgressStepper";
import { useDispatch, useSelector } from "react-redux";
import CircularLoader from "../../Common/CircularLoader";
import { convertDBNameToUILabel } from "../../../Helpers/NCRHelpers";
import { initiateSchema } from "../../../Helpers/schema";
import { fetchDashboardData } from "../../../Helpers/dashboardSlice";

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "14px",
          fontWeight: 400,
          color: "rgba(84, 84, 84, 1)",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
            opacity: 1,
          },
        },
      },
    },
  },
});

export default function InitiateComponent() {
  const lookups = useSelector((state) => state.lookup.data);
  const selectedFinancialYear = useSelector(state => state.dashboard.selectedFinancialYear);
  const [latestNCRId, setLatestNCRId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const accessToken = localStorage.getItem('NCProAccessToken');
  const initiateContainerRef = useRef(null);
  const [errors, setErrors] = useState();
  const dispatch = useDispatch();

  const departments = lookups?.["Department"] || [];
  const ncrTypes = lookups?.["NCR Type"] || [];
  const location = lookups?.["Location"] || [];
  const category = lookups?.["Non-conformance Category"] || [];
  const severity_or_priority_level = lookups?.["Severity or Priority Level"] || [];
  const responsibleTeam = lookups?.["Responsible Team/Person"] || [];

  const initialFormData = {
    ncr_id: '',
    date_of_issue: new Date().toISOString(),
    department: "",
    department_other: "",
    location: "",
    reported_by: "",
    ncr_type: "",
    category: "",
    affected_item_component_no: "",
    affected_item_revision: "",
    affected_item_qty: null,
    affected_item_description: "",
    affected_item_supplier_cust_name: "",
    affected_item_remarks: "",
    what_went_wrong: "",
    how_was_issue_identified: "",
    relevant_measurements_observations_specification: "",
    evidence_attachments: "",
    corrective_action_required: "Yes",
    // severity_or_priority_level: "",
    responsible_team_or_person: "",
    approving_authority: "Client admin",
    evidence_attachments_file_name: "",
  }
  const [formData, setFormData] = useState(initialFormData);
  const [aiResponses, setAiResponses] = useState({
    what_went_wrong: "",
    how_was_issue_identified: "",
  });

  const handleChange = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    const field = Object.keys(updatedData)[0];
    setErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const getNCRId = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/latestId`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setLatestNCRId(response.data.latestId);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (latestNCRId) {
      setFormData((prev) => ({
        ...prev,
        ncr_id: latestNCRId,
      }));
    }
  }, [latestNCRId]);

  useEffect(() => {
    getNCRId();
  }, [getNCRId]);

  const handleInitiateClick = async () => {
    let newErrors = {};
    const requiredFields = [
      "department",
      "reported_by",
      "ncr_type",
      "location",
      "category",
      "affected_item_component_no",
      "affected_item_revision",
      "affected_item_qty",
      "affected_item_description",
      "affected_item_supplier_cust_name",
      "what_went_wrong",
      "how_was_issue_identified",
      // "severity_or_priority_level",
      "responsible_team_or_person",
    ];

    if (formData.department === "Other") {
      requiredFields.push("department_other");
    }

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field] === "") {
        newErrors[field] = `${convertDBNameToUILabel(field)} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      const { ncr_id, evidence_attachments_file_name, evidence_attachments, ...formDataToSend } = formData;
      await initiateSchema.parseAsync(formData);
      const formDataPayload = new FormData();

      Object.keys(formDataToSend).forEach((key) => {
        formDataPayload.append(key, formDataToSend[key]);
      });
      if (evidence_attachments) {
        formDataPayload.append("evidence_attachments", evidence_attachments);
      }

      const response = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/initiate`,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      if (response.data.success) {
        toast.success(response.data.message || "Form submitted Successfully");
        const nextFormId = parseInt(formData.ncr_id) + 1;
        setFormData({ ...initialFormData, ncr_id: nextFormId, date_of_issue: new Date().toISOString() });
        if (initiateContainerRef.current) {
          initiateContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
        setAiResponses({ what_went_wrong: "", how_was_issue_identified: "" });
        dispatch(fetchDashboardData({
          startDate: selectedFinancialYear.startDate,
          endDate: selectedFinancialYear.endDate
        }));
      } else {
        toast.error(response.data.message || "Failed to submit");
      }
    } catch (error) {
      console.error('Error during form submission:', error.response?.data || error.errors[0]?.message);
      toast.error(error.response?.data || error.errors[0]?.message || "Something went wrong");
    }
  }

  return (
    <>
      {isLoading ? (
        <CircularLoader label="NCRDetails" />
      ) : (
        <ThemeProvider theme={theme}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 14 }}>
            <Box sx={{
              width: 24,
              height: 24,
            }}>
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '18px',
                  fontWeight: 700,
                }}
              >
                Initiate
              </Typography>
            </Box>
            <Box>
              <ProgressStepper status={['inProgress', 'notStarted', 'notStarted', 'notStarted']} />
            </Box>
          </Box>
          <Box
            ref={initiateContainerRef}
            sx={{
              height: '75vh',
              overflow: 'auto',
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: '#fff',
              marginTop: '16px',
              '&::-webkit-scrollbar': {
                width: '7px',
                height: '7px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(211, 211, 211, 1)',
                borderRadius: '5px',
              }
            }}
          >
            <Box sx={{ width: '100%', mt: 2 }}>
              <GeneralInformationBox content={{ label: "initiate", formData: formData, inputDisable: false, lookupTypes: { departments, ncrTypes, location, category }, errors: errors }} onChange={handleChange} />
              <AffectedItems content={{ formData: formData, inputDisable: false, errors: errors }} onChange={handleChange} />
              <NonConformanceDescription content={{ formData: formData, errors: errors }} onChange={handleChange} aiResponses={aiResponses} setAiResponses={setAiResponses} />
              <AdditionalInformation content={{ label: "initiate", formData: formData, lookupTypes: { severity_or_priority_level, responsibleTeam }, errors: errors }} onChange={handleChange} />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: 3, py: 2,
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderRadius: '0px 0px 10px 10px',
                boxShadow: '0px -1px 4px 0px rgba(0, 0, 0, 0.15)'
              }}>
                <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="neutral"
                    sx={{ fontFamily: 'Lato', fontWeight: 400, fontSize: '14px', border: '1px solid rgba(14, 41, 102, 1)', textTransform: 'none', borderRadius: '10px', color: 'rgba(14, 41, 102, 1)', py: 1 }}
                    onClick={() => {
                      setFormData({ ...initialFormData, ncr_id: formData.ncr_id, date_of_issue: new Date().toISOString() });
                      setAiResponses({ what_went_wrong: "", how_was_issue_identified: "" });
                    }}
                  >
                    Reset
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="neutral"
                    sx={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '14px', background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none', color: 'rgba(255, 255, 255, 1)', borderRadius: '10px', py: 1, px: 4 }}
                    onClick={handleInitiateClick}
                  >
                    Initiate
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      )}
    </>
  );
}
